import { RefObject, useEffect, useRef, useState } from 'react';
import { ArrowUpRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import BrandImage from './brand-image';
import { BUILDER_TOOLS } from './builder-tools';
import CtaChevron from './cta-chevron';
import { externalLinkProps } from './external-link';
import { SearchFaq } from './search-faqs';
import { MIN_QUERY_LENGTH, SuggestionStatus } from './use-search-products';

// A single product preview (shape returned by /api/search-suggest).
export interface SearchSuggestion {
  image?: string;
  name: string;
  price: string;
  href: string;
}

// Figma's second product column holds three rows, then the builder card fills the space of the rest.
const FIRST_COLUMN_ROWS = 5;
const SECOND_COLUMN_ROWS = 3;

const HEADING = 'text-14 text-white/80';
// Questions are links, so they take the blue tint: Viewrail blue-400 (#468CE4) clears AA on the #212121
// panel where the #2B70CA CTA blue doesn't; blue-300 (#87B5EF) for hover and the open question.
const FAQ_LINK = 'text-left text-15 leading-snug transition-colors hover:text-[#87B5EF]';

interface SearchPanelProps {
  open: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  inputRef: RefObject<HTMLInputElement>;
  /** Typeahead hits for the current query, or the popular products before anything is typed. */
  products: SearchSuggestion[];
  /** State of the typeahead request for the current query (see useSearchSuggestions). */
  status: SuggestionStatus;
  faqs: SearchFaq[];
  onNavigate: () => void;
  /** Load product thumbnails now rather than lazily (set once the shopper reaches for search). */
  eagerImages?: boolean;
}

// What to say when a query has no products to show, by request state. "No products match" is only
// ever said after a successful empty response, never while typing, loading or after a failure.
export const SearchStatusMessage = ({ status, term }: { status: SuggestionStatus; term: string }) => {
  if (status === 'loading' || status === 'idle') {
    return (
      <p role="status" className="text-15 text-white/70">
        Searching…
      </p>
    );
  }
  return (
    <p role="status" className="text-15 text-white/70">
      {status === 'error'
        ? 'Product suggestions are unavailable right now. See all results to search the full catalog.'
        : `No products match “${term}” yet.`}
    </p>
  );
};

// Shared with the phone search sheet (menu.tsx). 15/14px everywhere: the Figma's 13/12px read too small.
export const ProductRow = ({
  item,
  onNavigate,
  eager = false,
}: {
  item: SearchSuggestion;
  onNavigate: () => void;
  eager?: boolean;
}) => {
  // The accessible name is the product name alone, leaving out the price.
  const { name } = item;
  return (
    <li>
      <a href={item.href} onClick={onNavigate} aria-label={name} className="group flex items-center gap-16">
        <span className="flex size-62 shrink-0 items-center justify-center overflow-hidden bg-white/10">
          {item.image ? (
            <BrandImage
              src={item.image}
              alt=""
              width={200}
              grayBackground
              loading={eager ? 'eager' : 'lazy'}
              className="size-full object-cover"
            />
          ) : null}
        </span>
        <span className="flex min-w-0 flex-col gap-4">
          <span className="line-clamp-2 text-15 text-white group-hover:underline xl:text-15">{name}</span>
          {item.price && <span className="text-14 text-white/70">{item.price}</span>}
        </span>
      </a>
    </li>
  );
};

// The builders are the one thing in the panel that isn't a product, so the card breaks from the quiet
// grey rows: a brand-blue header band and an icon row per tool. Flat, no shadow. Phones get a
// sideways chip row instead (menu.tsx), built from the same BUILDER_TOOLS.
const BuilderToolsCard = ({ onNavigate }: { onNavigate: () => void }) => (
  <section className="mt-6 max-w-302 overflow-hidden border border-white/15 bg-white/[0.06]">
    {/* One-line header band; each tool row stays at a 44px hit area with its tagline beside the name. */}
    <div className="flex items-baseline gap-8 bg-[#2B70CA] px-14 py-8">
      <h2 className="font-gt-s text-13 font-bold">Design it yourself</h2>
      <p className="truncate text-11 text-white/80">Free builders, live pricing</p>
    </div>
    <ul className="divide-y divide-white/10">
      {BUILDER_TOOLS.map(({ label, href, tagline, Icon }) => (
        <li key={label}>
          <a
            href={href}
            {...externalLinkProps(href)}
            onClick={onNavigate}
            className="group flex min-h-44 items-center gap-10 px-14 py-6 transition-colors hover:bg-white/[0.08]"
          >
            <span className="flex size-28 shrink-0 items-center justify-center bg-white/10 text-white transition-colors group-hover:bg-[#2B70CA]">
              <Icon aria-hidden className="size-18" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="text-13 font-medium text-white">{label}</span>
              <span className="truncate text-11 text-white/60">{tagline}</span>
            </span>
            <ArrowUpRightIcon
              aria-hidden
              className="size-14 shrink-0 text-white/40 transition-colors group-hover:text-white"
            />
          </a>
        </li>
      ))}
    </ul>
  </section>
);

// Desktop search panel (Figma "Shop Banner - Search", 244:2): search + FAQs on the left, product
// previews in two columns (popular products on open, replaced by results as the shopper types), and the builder tools card closing out the second column.
const SearchPanel = ({
  open,
  query,
  onQueryChange,
  onSubmit,
  inputRef,
  products,
  status,
  faqs,
  onNavigate,
  eagerImages = false,
}: SearchPanelProps) => {
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Closed, the panel is only collapsed to 0 height; `inert` also takes its links and buttons out of
  // the tab order and the accessibility tree. Set on the element rather than as a JSX prop: the App
  // Router's bundled React 19 treats `inert` as a boolean (so "" is off) while React 18, used by the
  // tests, only renders string values, and no single prop value works in both.
  useEffect(() => {
    panelRef.current?.toggleAttribute('inert', !open);
  }, [open]);
  const term = query.trim();

  const firstColumn = products.slice(0, FIRST_COLUMN_ROWS);
  const secondColumn = products.slice(FIRST_COLUMN_ROWS, FIRST_COLUMN_ROWS + SECOND_COLUMN_ROWS);

  return (
    <div
      ref={panelRef}
      className={`overflow-y-auto transition-[max-height,opacity] ease-out ${
        open ? 'max-h-[calc(100vh-64px)] opacity-100 duration-500' : 'max-h-0 opacity-0 duration-300'
      }`}
    >
      <div className="grid grid-cols-[minmax(280px,1fr)_minmax(0,1.1fr)_minmax(0,1.1fr)] gap-x-38 px-42 pb-44 pt-32">
        {/* Left: search input, then the questions that give the empty state a starting point. */}
        <div className="flex flex-col">
          <form onSubmit={onSubmit} className="flex items-center gap-40">
            <MagnifyingGlassIcon className="size-20 shrink-0 text-white/75" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                onQueryChange(e.target.value);
                setExpandedQuestion(null);
              }}
              placeholder="Search shop.viewrail.com"
              aria-label="Search shop.viewrail.com"
              className="w-full appearance-none border-0 bg-transparent text-16 text-white outline-none placeholder:text-white/75"
            />
          </form>

          <div className="ml-60 mt-28 flex flex-col gap-40">
            {faqs.length > 0 && (
              <section className="flex flex-col gap-14">
                <h2 className={HEADING}>Common Questions:</h2>
                <ul className="flex max-w-340 flex-col gap-12">
                  {faqs.map((faq) => {
                    const expanded = expandedQuestion === faq.question;
                    return (
                      <li key={faq.question}>
                        <button
                          type="button"
                          aria-expanded={expanded}
                          onClick={() => setExpandedQuestion(expanded ? null : faq.question)}
                          className={`${FAQ_LINK} ${expanded ? 'text-[#87B5EF]' : 'text-[#468CE4]'}`}
                        >
                          {faq.question}
                        </button>
                        {expanded && (
                          <p className="mt-8 whitespace-pre-line text-14 leading-[1.5] text-white/75">{faq.answer}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>
        </div>

        {/* Middle + right: product previews under one label. Before a query these are the popular products,
            so the panel is never empty; typing swaps them for results. */}
        <div className="col-span-2 flex flex-col gap-14">
          <div className="flex items-baseline justify-between">
            <h2 className={HEADING}>{term.length >= MIN_QUERY_LENGTH ? 'Products' : 'Popular Products'}</h2>
          </div>
          <div className="grid grid-cols-2 gap-x-38">
            <div className="flex flex-col gap-18">
              {term.length >= MIN_QUERY_LENGTH && products.length === 0 ? (
                <SearchStatusMessage status={status} term={term} />
              ) : (
                <ul className="flex flex-col gap-18">
                  {firstColumn.map((item) => (
                    <ProductRow key={item.href} item={item} onNavigate={onNavigate} eager={eagerImages} />
                  ))}
                </ul>
              )}
              {term.length >= MIN_QUERY_LENGTH && status !== 'loading' && (
                <a
                  href={`/search/?q=${encodeURIComponent(term)}`}
                  onClick={onNavigate}
                  className="font-gt-s text-14 font-bold text-white/75 transition-colors hover:text-white"
                >
                  See all results for “{term}”
                  <CtaChevron />
                </a>
              )}
            </div>

            <div className="flex flex-col gap-18">
              <ul className="flex flex-col gap-18">
                {secondColumn.map((item) => (
                  <ProductRow key={item.href} item={item} onNavigate={onNavigate} eager={eagerImages} />
                ))}
              </ul>

              <BuilderToolsCard onNavigate={onNavigate} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPanel;
